import { Injectable } from '@angular/core';
import { IconKey } from '../../../../../whitelabel/icon-key.enum';
import { LanguageService } from '../../../../core/services/language.service';
import { OptionResolver } from '../../../../shared/components/card-select/contract/options-resolver.contract';
import { IconProviderService } from '../../../../whitelabel/services/icon-provider.service';
import { AssignedTelematicsUnit } from '../contract/key-value-pair.contract';
import { TelematicsSource } from '../enums/telematics-source.enum';
import { TelematicsUnitType } from '../enums/telematics-unit-type.enum';

@Injectable({ providedIn: 'root' })
export class EquipmentTelematicsUnitResolver implements OptionResolver {
  constructor(
    private languageService: LanguageService,
    private iconProviderService: IconProviderService,
  ) {}

  private static unitTypesWithProfiles = new Set<string>([
    TelematicsUnitType.DIGITAL_MATTER_OYSTER3_UNIT.valueOf(),
    TelematicsUnitType.DIGITAL_MATTER_BARRA_UNIT.valueOf(),
  ]);

  private static unitTypesWithTelemetry = new Set<string>([TelematicsUnitType.WACKER_NEUSON_STREAMING_API.valueOf()]);

  private static unitsThatDetectBeacons = new Set<string>([
    TelematicsUnitType.DIGITAL_MATTER_OYSTER3_UNIT.valueOf(),
    TelematicsUnitType.TELTONIKA_OBD_UNIT.valueOf(),
    TelematicsUnitType.TELTONIKA_CABLE_UNIT.valueOf(),
  ]);

  private static beaconTypes = new Set<string>([
    TelematicsUnitType.ELA_PUCK.valueOf(),
    TelematicsUnitType.ELA_COIN.valueOf(),
    TelematicsUnitType.CONFIDEX_CLASSIC.valueOf(),
    TelematicsUnitType.CONFIDEX_ROUGH.valueOf(),
    TelematicsUnitType.CONFIDEX_MICRO.valueOf(),
  ]);

  private static unitsThatDoNotSupportFaults = new Set<string>([
    ...EquipmentTelematicsUnitResolver.beaconTypes,
    TelematicsUnitType.DIGITAL_MATTER_OYSTER3_UNIT.valueOf(),
    TelematicsUnitType.DIGITAL_MATTER_BARRA_UNIT.valueOf(),
  ]);

  private static unitsThatDoNotSupportOperatingHours = new Set<string>([
    ...EquipmentTelematicsUnitResolver.beaconTypes,
    TelematicsUnitType.DIGITAL_MATTER_OYSTER3_UNIT.valueOf(),
    TelematicsUnitType.DIGITAL_MATTER_BARRA_UNIT.valueOf(),
  ]);

  public resolveName(option: string): string {
    switch (option) {
      case TelematicsUnitType.AEMP:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.iso');
      case TelematicsUnitType.GENERIC_2:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.aemp');
      case TelematicsUnitType.GENERIC_3:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.goldfunk');
      case TelematicsUnitType.GENERIC_4:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.sinos');
      case TelematicsUnitType.GENERIC_5:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.twinline');
      case TelematicsUnitType.SIGFOX_TRACKERTYPL26_1:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.sigfox');
      case TelematicsUnitType.TRACKUNIT_1:
      case TelematicsUnitType.TRACKUNIT_1_RAW:
      case TelematicsUnitType.TRACKUNIT_1_SPOT:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.trackunit');
      case TelematicsUnitType.TELTONIKA_CABLE_UNIT:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.cableUnit');
      case TelematicsUnitType.TELTONIKA_OBD_UNIT:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.obdUnit');
      case TelematicsUnitType.GPS_OVER_IP:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.gps');
      case TelematicsUnitType.RIO:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.rio');
      case TelematicsUnitType.CONFIDEX_CLASSIC:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.confidexClassic');
      case TelematicsUnitType.CONFIDEX_ROUGH:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.confidexTough');
      case TelematicsUnitType.CONFIDEX_MICRO:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.confidexMicro');
      case TelematicsUnitType.DIGITAL_MATTER_OYSTER3_UNIT:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.oyster3');
      case TelematicsUnitType.DIGITAL_MATTER_BARRA_UNIT:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.barra');
      case TelematicsUnitType.WACKER_NEUSON_STREAMING_API:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.wackerNeusonStreamingApi');
      case TelematicsSource.IMPORTED:
      case TelematicsSource.MANUAL:
      case TelematicsSource.TELEMATICS:
      case TelematicsSource.CALIBRATION:
      case TelematicsSource.NATIVE_APP_IMPORTED:
        return this.languageService.getInstant('modules.telematic.sourceEnum.' + option);
    }

    return option;
  }

  public resolveSubtitle(option: string): string {
    switch (option) {
      case TelematicsUnitType.TRACKUNIT_1_RAW:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.raw');
      case TelematicsUnitType.TRACKUNIT_1_SPOT:
        return this.languageService.getInstant('modules.equipment.assignTelematicUnit.spot');
      default:
        return '';
    }
  }

  public resolveIcon(option: string): string {
    switch (option) {
      // case TelematicsUnitType.AEMP:
      // case TelematicsUnitType.GENERIC_2:
      // case TelematicsUnitType.GENERIC_3:
      // case TelematicsUnitType.GENERIC_4:
      // case TelematicsUnitType.GENERIC_5:
      // case TelematicsUnitType.GPS_OVER_IP:
      // case TelematicsUnitType.RIO:
      default:
        return this.iconProviderService.getIcon(IconKey.EquipmentTelematicUnitDefault); // TODO: different icon
    }
  }

  public resolveLogo(option: string): string {
    switch (option) {
      case TelematicsUnitType.SIGFOX_TRACKERTYPL26_1:
        return './assets/images/telematic-unit/logos/sigfox.svg';
      case TelematicsUnitType.TRACKUNIT_1:
      case TelematicsUnitType.TRACKUNIT_1_RAW:
      case TelematicsUnitType.TRACKUNIT_1_SPOT:
      case TelematicsUnitType.TRACKUNIT_1_SPOT_RAW:
        return './assets/images/telematic-unit/logos/trackunit.svg';
      case TelematicsUnitType.CONFIDEX_CLASSIC:
      case TelematicsUnitType.CONFIDEX_ROUGH:
        return './assets/images/telematic-unit/logos/confidex_tough.png';
      case TelematicsUnitType.CONFIDEX_MICRO:
        return './assets/images/telematic-unit/logos/confidex_micro.png';
      case TelematicsUnitType.TELTONIKA_CABLE_UNIT:
        return './assets/images/telematic-unit/logos/teltonika_cable.png';
      case TelematicsUnitType.TELTONIKA_OBD_UNIT:
        return './assets/images/telematic-unit/logos/teltonika_obd.png';
      case TelematicsUnitType.DIGITAL_MATTER_OYSTER3_UNIT:
        return './assets/images/telematic-unit/logos/oyster3-4g-bluetooth.png';
      case TelematicsUnitType.DIGITAL_MATTER_BARRA_UNIT:
        return './assets/images/telematic-unit/logos/barra.png';
      default:
        return '';
    }
  }

  public resolveIconImage(option: string): string {
    switch (option) {
      case TelematicsUnitType.TRACKUNIT_1_RAW:
        return './assets/images/telematic-unit/logos/trackunit_raw.png';
      case TelematicsUnitType.TRACKUNIT_1_SPOT:
        return './assets/images/telematic-unit/logos/trackunit_spot.png';
      default:
        return '';
    }
  }

  public getTelematicsUnitInfo(assignment: AssignedTelematicsUnit): string {
    return `${this.getTelematicsUnitType(assignment)}: ${this.getTelematicsUnitId(assignment)}`;
  }

  public getTelematicsUnitType(assignment: AssignedTelematicsUnit): string {
    const key = assignment.key as keyof typeof TelematicsUnitType;
    if (TelematicsUnitType[key]) {
      switch (key) {
        case TelematicsUnitType.AEMP:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.iso');
        case TelematicsUnitType.GENERIC_2:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.aemp');
        case TelematicsUnitType.GENERIC_3:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.goldfunk');
        case TelematicsUnitType.GENERIC_4:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.sinos');
        case TelematicsUnitType.GENERIC_5:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.twinline');
        case TelematicsUnitType.SIGFOX_TRACKERTYPL26_1:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.sigfox');
        case TelematicsUnitType.TRACKUNIT_1:
        case TelematicsUnitType.TRACKUNIT_1_RAW:
        case TelematicsUnitType.TRACKUNIT_1_SPOT:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.trackunit');
        case TelematicsUnitType.TELTONIKA_CABLE_UNIT:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.cableUnit');
        case TelematicsUnitType.TELTONIKA_OBD_UNIT:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.obdUnit');
        case TelematicsUnitType.GPS_OVER_IP:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.gps');
        case TelematicsUnitType.RIO:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.rio');
        case TelematicsUnitType.CONFIDEX_CLASSIC:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.confidexClassic');
        case TelematicsUnitType.CONFIDEX_ROUGH:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.confidexTough');
        case TelematicsUnitType.CONFIDEX_MICRO:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.confidexMicro');
        case TelematicsUnitType.DIGITAL_MATTER_OYSTER3_UNIT:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.oyster3');
        case TelematicsUnitType.DIGITAL_MATTER_BARRA_UNIT:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.barra');
        case TelematicsUnitType.WACKER_NEUSON_STREAMING_API:
          return this.languageService.getInstant('modules.equipment.assignTelematicUnit.wackerNeusonStreamingApi');
        default:
          return TelematicsUnitType[key];
      }
    } else {
      return this.languageService.getInstant('general.unknown');
    }
  }

  public getTelematicsUnitId(assignment: AssignedTelematicsUnit): string {
    const key = assignment?.key as keyof typeof TelematicsUnitType;
    if (TelematicsUnitType[key]) {
      switch (key) {
        case TelematicsUnitType.CONFIDEX_CLASSIC:
        case TelematicsUnitType.CONFIDEX_ROUGH:
        case TelematicsUnitType.CONFIDEX_MICRO:
          return assignment.value.split('##').pop() ?? '';
        default:
          return assignment.value;
      }
    } else {
      return assignment.value;
    }
  }

  public getUnitIdGuiName(telematicsUnitType: string): string {
    return EquipmentTelematicsUnitResolver.isBeaconTelematicUnit(telematicsUnitType) ||
      telematicsUnitType === TelematicsUnitType.WACKER_NEUSON_STREAMING_API
      ? this.languageService.getInstant('modules.equipment.base.serialNumber')
      : this.languageService.getInstant('modules.equipment.base.imei');
  }

  public static doesSupportProfiles(telematicsUnitType: TelematicsUnitType): boolean {
    return this.unitTypesWithProfiles.has(telematicsUnitType);
  }

  public static isBeaconTelematicUnit(telematicsUnitType: string): boolean {
    return this.beaconTypes.has(telematicsUnitType);
  }

  public static canDetectBeacons(telematicsUnitType: string): boolean {
    return this.unitsThatDetectBeacons.has(telematicsUnitType);
  }

  public static canHaveFaults(telematicsUnitType: string): boolean {
    return !this.unitsThatDoNotSupportFaults.has(telematicsUnitType);
  }

  public static canHaveOperatingHours(telematicsUnitType: string): boolean {
    return !this.unitsThatDoNotSupportOperatingHours.has(telematicsUnitType);
  }

  public static doesHaveTelemetry(telematicsUnitType: TelematicsUnitType): boolean {
    return this.unitTypesWithTelemetry.has(telematicsUnitType);
  }

  public static showTelematicsDataCharts(telematicUnitType: string): boolean {
    return !this.isBeaconTelematicUnit(telematicUnitType) && this.canHaveOperatingHours(telematicUnitType);
  }
}
